import {
  useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  KeyboardKeys, useKeyPress,
} from '~/_shared/utils/hooks/useKeyPress';
import { useLocationFromGeolocation } from '~/_shared/utils/hooks/useLocationFromGeolocation';
import {
  setSelectLocationAddressIsLoading, stopSelectLocationTool,
} from '~/store/frontendState/mapTools/selectLocation/selectLocation.actionCreators';
import {
  useSelectLocationConfigurationSelector, useSelectLocationIsActiveSelector,
} from '~/store/frontendState/mapTools/selectLocation/selectLocation.selectors';
import { SelectLocationConfiguration } from '~/store/frontendState/mapTools/selectLocation/selectLocation.state';
import { placesToolSetStartingLocation } from '~/store/frontendState/places/places.actionCreators';
import { setStartingLocation } from '~/store/frontendState/proximityTool/proximityTool.actionCreators';

export const useSelectLocation = (map?: google.maps.Map) => {
  const dispatch = useDispatch();
  const selectLocationActive = useSelectLocationIsActiveSelector();
  const selectLocationConfiguration = useSelectLocationConfigurationSelector();
  const { addLocationFromLatLng, getGeolocationFromLatLng } = useLocationFromGeolocation();

  const handleStopSelectLocationTool = useCallback(() => dispatch(stopSelectLocationTool()), [dispatch]);

  useKeyPress(useMemo(() => ({
    callbacks: { onKeyPress: handleStopSelectLocationTool },
    options: { allowInputs: true },
    targetKeys: KeyboardKeys.Escape,
  }), [handleStopSelectLocationTool]));

  useEffect(() => () => {
    handleStopSelectLocationTool();
  }, [handleStopSelectLocationTool]);

  useEffect(() => {
    if (!selectLocationActive || !map || !selectLocationConfiguration) {
      return;
    }
    const listener = google.maps.event.addListener(map, 'click', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) {
        return;
      }

      listener?.remove();

      const latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      const handleLoadingChange = (newValue: boolean) => {
        dispatch(setSelectLocationAddressIsLoading(newValue));
      };

      if (selectLocationConfiguration === SelectLocationConfiguration.AddLocation) {
        addLocationFromLatLng({
          latLng,
          onIsLoadingChanged: handleLoadingChange,
        });
      }
      else if (selectLocationConfiguration === SelectLocationConfiguration.SetProximityToolStartLocation) {
        getGeolocationFromLatLng({
          latLng,
          onIsLoadingChanged: handleLoadingChange,
          callback: (response) => {
            dispatch(setStartingLocation(response));
          },
        });
      }
      else if (selectLocationConfiguration === SelectLocationConfiguration.SetPlacesToolStartLocation) {
        getGeolocationFromLatLng({
          latLng,
          onIsLoadingChanged: handleLoadingChange,
          callback: (response) => {
            dispatch(placesToolSetStartingLocation(response));
          },
        });
      }

      dispatch(stopSelectLocationTool());
    });

    return () => {
      listener?.remove();
    };

  }, [addLocationFromLatLng, dispatch, getGeolocationFromLatLng, map, selectLocationActive, selectLocationConfiguration]);
};
